import { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { chakra, HStack, Spacer, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { TeamMemberStatus } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import PayPerSeatMarketingCallout from 'features/Team/components/PayPerSeatMarketingCallout';
import * as Layout from 'layouts/StickyStepperLayout';

import { useMemberInviteState } from '../../../../MemberInviteProvider';
import { useTeamMemberEmailExists } from '../hooks/useTeamMemberEmailExists';
import locales from './locales';
import { MemberAlreadyExistsCallout } from './MemberAlreadyExistCallout';
import {
  MemberInformationFormSchema,
  memberInformationFormSchema,
} from './schema';

interface MemberInformationFormProps {
  handleCancel: () => void;
  onBackToLockedUsers: () => void;
  onEditExistingUser: (companyUserId: string) => void;
  onRevokedMembersEditClicked: (companyUserId: string) => void;
  onSubmit: SubmitHandler<MemberInformationFormSchema>;
}

const MemberInformationForm: FC<MemberInformationFormProps> = ({
  handleCancel,
  onBackToLockedUsers,
  onEditExistingUser,
  onRevokedMembersEditClicked,
  onSubmit,
}) => {
  const {
    isFreeRolesOnly,
    memberInformation,
    selectedLegalBeneficiary: legalBeneficiaryInvite,
    shouldShowLegalBeneficiarySelector,
  } = useMemberInviteState();
  const form = useForm<MemberInformationFormSchema>({
    defaultValues: memberInformation,
    mode: 'onBlur',
    resolver: zodResolver(memberInformationFormSchema),
  });
  const { companyProfileId } = useCompanyContext();
  const {
    emailExists,
    firstTeamMember,
    loading: searchingExistingEmails,
  } = useTeamMemberEmailExists({
    companyProfileId,
    email: form.watch('inviteeEmail'),
  });

  const { isSubmitting, isValid } = form.formState;
  const isLegalBeneficiaryInvite = legalBeneficiaryInvite !== false;

  return (
    <chakra.form display="contents" onSubmit={form.handleSubmit(onSubmit)}>
      <Layout.ContentsContainer gap="space-24">
        <VStack align="flex-start" spacing="space-16">
          <Heading size="lg">
            {isFreeRolesOnly ? locales.titleAccountOnly : locales.title}
          </Heading>
          {!shouldShowLegalBeneficiarySelector && !isFreeRolesOnly ? (
            <PayPerSeatMarketingCallout />
          ) : null}
        </VStack>
        <HStack>
          <VStack alignItems="stretch" width="full">
            <Form.Field
              error={form.formState.errors.inviteeFirstName?.message}
              label={locales.labels.inviteeFirstName}
            >
              <Form.Input
                disabled={isLegalBeneficiaryInvite}
                placeholder={locales.placeholders.inviteeFirstName}
                textTransform="capitalize"
                {...form.register('inviteeFirstName')}
              />
            </Form.Field>
          </VStack>
          <VStack alignItems="stretch" width="full">
            <Form.Field
              error={form.formState.errors.inviteeLastName?.message}
              label={locales.labels.inviteeLastName}
            >
              <Form.Input
                _placeholderShown={{ textTransform: 'initial' }}
                disabled={isLegalBeneficiaryInvite}
                placeholder={locales.placeholders.inviteeLastName}
                textTransform="uppercase"
                {...form.register('inviteeLastName')}
              />
            </Form.Field>
          </VStack>
        </HStack>
        <VStack alignItems="stretch">
          <Form.Field
            error={
              emailExists && !searchingExistingEmails
                ? locales.errors.emailAlreadyUsed
                : form.formState.errors.inviteeEmail?.message
            }
            label={locales.labels.inviteeEmail}
          >
            <Form.Input
              disabled={isLegalBeneficiaryInvite}
              placeholder={locales.placeholders.inviteeEmail}
              {...form.register('inviteeEmail')}
            />
          </Form.Field>
        </VStack>
        {emailExists && !searchingExistingEmails ? (
          <MemberAlreadyExistsCallout
            firstName={firstTeamMember?.firstName ?? ''}
            hasOngoingInvitation={
              firstTeamMember?.hasOngoingInvitation ?? false
            }
            isLocked={firstTeamMember?.status === TeamMemberStatus.Locked}
            lastName={firstTeamMember?.lastName ?? ''}
            onBackToLockedUsers={onBackToLockedUsers}
            onCancel={handleCancel}
            onEditExistingUser={() =>
              firstTeamMember
                ? onEditExistingUser(firstTeamMember.companyUserOrInvitationId)
                : null
            }
            onEditLockedUser={() =>
              firstTeamMember
                ? onRevokedMembersEditClicked(
                    firstTeamMember.companyUserOrInvitationId,
                  )
                : null
            }
          />
        ) : null}
      </Layout.ContentsContainer>
      <Layout.FooterContainer>
        <StepperProgressBar />

        <Layout.FooterButtonsContainer>
          <Button onClick={handleCancel} variant="inline-secondary">
            {locales.cancel}
          </Button>
          <Spacer />
          <Button
            isDisabled={
              !isValid || isSubmitting || searchingExistingEmails || emailExists
            }
            isLoading={isSubmitting || searchingExistingEmails}
            type="submit"
          >
            {locales.submit}
          </Button>
        </Layout.FooterButtonsContainer>
      </Layout.FooterContainer>
    </chakra.form>
  );
};

export default MemberInformationForm;
